import { SessionTimeoutModal } from '@/components/SessionTimeoutModal';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import {
  ClinicBasicInfo,
  ClinicJoinRequestView,
  LoginCredentials,
  User,
} from '@/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { supportsAllLocations } from '@/lib/allLocations';
import { identifyUser, resetAnalytics } from '../lib/analytics';
import {
  attemptTokenRefresh,
  login as apiLogin,
  logout as apiLogout,
  updateUserProfile as apiUpdateUserProfile,
  fetchPendingClinicInvitations,
  getCurrentUser,
  getMyClinics,
  getMyJoinRequest,
} from '../lib/apiClient';
import { clearRecentClinics } from '../lib/recentClinics';
import { safeLocalStorage, safeSessionStorage } from '../lib/safeStorage';

const ACTIVE_CLINIC_ID_STORAGE_KEY = 'activeClinicId';
const ALL_LOCATIONS_STORAGE_KEY = 'allLocationsActive';

// Key for the email of the last successfully logged-in user. Intentionally
// NOT cleared on logout — Login.tsx reads it to pre-fill the email field so
// returning users on this device only have to type their password.
export const LAST_LOGIN_EMAIL_STORAGE_KEY = 'lastLoginEmail';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessibleClinics: ClinicBasicInfo[];
  /**
   * Null while `allLocationsActive`: a page showing every location has no
   * acting clinic, so a clinic-scoped write there refuses instead of using a
   * clinic the user cannot see. Pages that work on one clinic get the stored one.
   */
  activeClinicId: string | null;
  activeClinicName: string | null;
  setActiveClinicId: (clinicId: string | null) => void;
  /**
   * A non-admin with two or more clinics. Admins are excluded: their
   * accessible clinics are every clinic in the system.
   */
  canViewAllLocations: boolean;
  /**
   * The user prefers every location and the current page supports it
   * (`supportsAllLocations`); never true without the above.
   */
  allLocationsActive: boolean;
  setAllLocationsActive: (active: boolean) => void;
  /** The clinics the current view covers: every one, or the active one. */
  viewedClinicIds: string[];
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: (options?: { skipRedirect?: boolean }) => Promise<void>;
  updateUserProfile: (updateData: {
    firstName?: string;
    lastName?: string;
    workPhoneNumber?: string;
    titleOrRole?: string;
  }) => Promise<User>;
  refetchMyClinics: () => Promise<void>;
  refetchCurrentUser: () => Promise<void>;
  numPendingClinicInvitations: number;
  refetchNumPendingClinicInvitations: () => Promise<void>;
  /**
   * The user's clinic join request (Project A) — fetched only for verified
   * zero-clinic users, so legacy zero-clinic accounts (null) stay untouched.
   * Drives the /pending gate in ProtectedRoute and the PendingJoinPage.
   */
  joinRequest: ClinicJoinRequestView | null;
  /**
   * True when the join-request lookup errored (network/server), as opposed to
   * "no request exists" (404 → joinRequest null, this false). The gate fails
   * SAFE on it: a matched pending user with a transient error is routed to
   * /pending (which has retry UI) instead of an empty clinic-less app.
   */
  joinRequestLookupFailed: boolean;
  refetchJoinRequest: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessibleClinics, setAccessibleClinics] = useState<ClinicBasicInfo[]>(
    []
  );
  const [numPendingClinicInvitations, setNumPendingClinicInvitations] =
    useState(0);
  const [joinRequest, setJoinRequest] = useState<ClinicJoinRequestView | null>(
    null
  );
  const [joinRequestLookupFailed, setJoinRequestLookupFailed] = useState(false);
  const [activeClinicId, setActiveClinicIdState] = useState<string | null>(() =>
    safeLocalStorage.getItem(ACTIVE_CLINIC_ID_STORAGE_KEY)
  );
  // Defaults on: login sets it, and a session from before it existed opens
  // on All locations too.
  const [allLocationsPreferred, setAllLocationsPreferred] = useState(
    () => safeLocalStorage.getItem(ALL_LOCATIONS_STORAGE_KEY) !== 'false'
  );
  const canViewAllLocations =
    !!user && user.role !== 'ADMIN' && accessibleClinics.length > 1;
  const allLocationsActive =
    allLocationsPreferred &&
    canViewAllLocations &&
    supportsAllLocations(pathname);

  const viewedClinicIds = useMemo(() => {
    if (allLocationsActive) return accessibleClinics.map(clinic => clinic.id);
    return activeClinicId ? [activeClinicId] : [];
  }, [allLocationsActive, accessibleClinics, activeClinicId]);

  // Fetch the join request only for verified zero-clinic users — the guard
  // 401s unverified users, and users with clinics never have a pending gate.
  // A lookup ERROR is tracked separately from "no request" (404): the gate
  // fails safe on it, routing to /pending's retry UI rather than dropping a
  // matched pending user into an empty clinic-less app.
  const syncJoinRequest = useCallback(async (currentUser: User | null) => {
    if (
      currentUser &&
      currentUser.clinicIds.length === 0 &&
      !currentUser.emailVerificationRequired
    ) {
      try {
        setJoinRequest(await getMyJoinRequest());
        setJoinRequestLookupFailed(false);
      } catch {
        setJoinRequest(null);
        setJoinRequestLookupFailed(true);
      }
    } else {
      setJoinRequest(null);
      setJoinRequestLookupFailed(false);
    }
  }, []);

  const activeClinicName = useMemo(() => {
    if (!activeClinicId) return null;
    const activeClinic = accessibleClinics.find(
      clinic => clinic.id === activeClinicId
    );
    return activeClinic?.name || null;
  }, [activeClinicId, accessibleClinics]);

  useEffect(() => {
    async function loadUserAndClinics() {
      try {
        // Verify with server first - don't use localStorage for initial auth state
        // as it may contain stale data from a previous session/user, causing
        // incorrect routing decisions (e.g., flashing email verification page)
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);

          // Fetch clinics for the user
          const clinics = await getMyClinics();
          setAccessibleClinics(clinics);

          const pendingClinicInvitations = await fetchPendingClinicInvitations({
            page: 1,
            limit: 1, // Get only count
          });
          setNumPendingClinicInvitations(pendingClinicInvitations.totalItems);

          // Resolve the pending-gate input before isLoading flips so
          // ProtectedRoute never routes on a stale null.
          await syncJoinRequest(currentUser);

          identifyUser(currentUser.id, {
            name: `${currentUser.firstName} ${currentUser.lastName}`,
            email: currentUser.email,
            role: currentUser.role,
          });

          const storedClinicId = safeLocalStorage.getItem(
            ACTIVE_CLINIC_ID_STORAGE_KEY
          );
          if (storedClinicId && clinics.some(c => c.id === storedClinicId)) {
            setActiveClinicIdState(storedClinicId);
          } else if (clinics.length > 0) {
            // Set first clinic as default if no valid stored clinic
            setActiveClinicIdState(clinics[0].id);
            safeLocalStorage.setItem(
              ACTIVE_CLINIC_ID_STORAGE_KEY,
              clinics[0].id
            );
          } else {
            // No clinics available
            setActiveClinicIdState(null);
            safeLocalStorage.removeItem(ACTIVE_CLINIC_ID_STORAGE_KEY);
          }
        } else {
          setUser(null);
          setAccessibleClinics([]);
          setActiveClinicIdState(null);
          setJoinRequest(null);
          setJoinRequestLookupFailed(false);
          safeLocalStorage.removeItem('user');
          safeLocalStorage.removeItem(ACTIVE_CLINIC_ID_STORAGE_KEY);
        }
      } catch (error) {
        setUser(null);
        setAccessibleClinics([]);
        setActiveClinicIdState(null);
        setJoinRequest(null);
        setJoinRequestLookupFailed(false);
        safeLocalStorage.removeItem('user');
        safeLocalStorage.removeItem(ACTIVE_CLINIC_ID_STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    }

    loadUserAndClinics();
    // syncJoinRequest is a stable useCallback (empty deps), so this still
    // runs only once on mount.
  }, [syncJoinRequest]);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    const response = await apiLogin(credentials);
    // The remembered email (server-normalized, not the typed credential) only
    // powers the login form's pre-fill.
    safeLocalStorage.setItem(LAST_LOGIN_EMAIL_STORAGE_KEY, response.user.email);

    // Fetch supplementary data before setting any React state.
    // Setting state mid-function would trigger a re-render that causes
    // PublicOnlyRoute to redirect away from /login before onSubmit can
    // navigate, creating a race condition with stale redirect paths.
    const clinics = await getMyClinics();

    const pendingClinicInvitations = await fetchPendingClinicInvitations({
      page: 1,
      limit: 1, // Get only count
    });

    await syncJoinRequest(response.user);

    identifyUser(response.user.id, {
      name: `${response.user.firstName} ${response.user.lastName}`,
      email: response.user.email,
      role: response.user.role,
    });

    if (clinics.length > 0) {
      safeLocalStorage.setItem(ACTIVE_CLINIC_ID_STORAGE_KEY, clinics[0].id);
    }
    safeLocalStorage.setItem(ALL_LOCATIONS_STORAGE_KEY, 'true');

    // Batch all state updates together at the end so React renders once,
    // AFTER login() returns and onSubmit has already called navigate().
    setUser(response.user);
    setAccessibleClinics(clinics);
    setNumPendingClinicInvitations(pendingClinicInvitations.totalItems);
    if (clinics.length > 0) {
      setActiveClinicIdState(clinics[0].id);
    }
    setAllLocationsPreferred(true);

    return response.user;
  };

  const handleLogout = useCallback(
    async (options?: { skipRedirect?: boolean }) => {
      try {
        await apiLogout();
      } catch {
        // Best-effort — server session expires on its own if this fails
      } finally {
        if (!options?.skipRedirect) {
          // Mark that a logout just occurred so Login.tsx ignores any stale redirect
          // state that ProtectedRoute may have saved during the logout transition.
          safeSessionStorage.setItem('isPostLogout', 'true');
          safeSessionStorage.removeItem('redirectAfterLogin');
          navigate('/login', { replace: true });
        }

        setUser(null);
        setAccessibleClinics([]);
        setActiveClinicIdState(null);
        setJoinRequest(null);
        setJoinRequestLookupFailed(false);
        // 'user' is a legacy key that is no longer written; keep removing it to
        // purge copies persisted by older builds.
        safeLocalStorage.removeItem('user');
        safeLocalStorage.removeItem(ACTIVE_CLINIC_ID_STORAGE_KEY);
        safeLocalStorage.removeItem(ALL_LOCATIONS_STORAGE_KEY);
        setAllLocationsPreferred(true);
        // The next person on this browser may be a different admin — don't
        // leak the previous user's recently viewed clinics.
        clearRecentClinics();

        // Unlink the analytics/session-replay session from the user
        resetAnalytics();

        // The cache holds this session's patient data, and the next person to
        // sign in on this tab must never be shown any of it.
        queryClient.clear();
      }
    },
    [navigate, queryClient]
  );

  const setActiveClinicId = useCallback((clinicId: string | null) => {
    setActiveClinicIdState(clinicId);
    if (clinicId) {
      safeLocalStorage.setItem(ACTIVE_CLINIC_ID_STORAGE_KEY, clinicId);
    } else {
      safeLocalStorage.removeItem(ACTIVE_CLINIC_ID_STORAGE_KEY);
    }
  }, []);

  const setAllLocationsActive = useCallback((active: boolean) => {
    setAllLocationsPreferred(active);
    safeLocalStorage.setItem(ALL_LOCATIONS_STORAGE_KEY, String(active));
  }, []);

  const updateUserProfile = useCallback(
    async (updateData: {
      firstName?: string;
      lastName?: string;
      workPhoneNumber?: string;
      titleOrRole?: string;
    }) => {
      const updatedUser = await apiUpdateUserProfile(updateData);
      setUser(updatedUser);
      return updatedUser;
    },
    []
  );

  // Repair the active clinic whenever it's missing from the accessible list.
  // A session that mounts with zero clinics nulls activeClinicId, and only
  // the mount effect and login() default it — so a user gaining their first
  // membership mid-session (create-or-join on /pending, accepting an invite)
  // would otherwise keep a null sending clinic until a reload. Reconciling
  // committed state here (rather than inside refetchMyClinics) means a
  // refetch that resolves after the user switches clinics can't undo the
  // newer selection, and an empty list is left alone so a transient empty
  // response never wipes a persisted selection.
  useEffect(() => {
    if (accessibleClinics.length === 0) return;
    if (
      activeClinicId &&
      accessibleClinics.some(clinic => clinic.id === activeClinicId)
    ) {
      return;
    }
    setActiveClinicId(accessibleClinics[0].id);
  }, [accessibleClinics, activeClinicId, setActiveClinicId]);

  const refetchMyClinics = useCallback(async () => {
    const clinics = await getMyClinics();
    setAccessibleClinics(clinics);
  }, [setAccessibleClinics]);

  const refetchCurrentUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        // Membership / verification may have changed — resync the pending-gate
        // input (e.g. right after email verification).
        await syncJoinRequest(currentUser);
      }
    } catch (error) {
      // If refetch fails, don't logout the user, just log the error
      console.error('Failed to refetch current user:', error);
    }
  }, [syncJoinRequest]);

  const refetchNumPendingClinicInvitations = useCallback(async () => {
    const pendingClinicInvitations = await fetchPendingClinicInvitations({
      page: 1,
      limit: 1, // Get only count
    });
    setNumPendingClinicInvitations(pendingClinicInvitations.totalItems);
  }, [setNumPendingClinicInvitations]);

  const refetchJoinRequest = useCallback(async () => {
    await syncJoinRequest(user);
  }, [syncJoinRequest, user]);

  const handleIdleTimeout = useCallback(async () => {
    await handleLogout();
  }, [handleLogout]);

  const handleStayLoggedIn = useCallback(async () => {
    await attemptTokenRefresh();
  }, []);

  const { showWarning, countdown, stayLoggedIn } = useIdleTimeout(
    handleIdleTimeout,
    handleStayLoggedIn,
    !!user
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin: user?.role === 'ADMIN',
        isLoading,
        isAuthenticated: !!user,
        accessibleClinics,
        activeClinicId: allLocationsActive ? null : activeClinicId,
        activeClinicName: allLocationsActive ? null : activeClinicName,
        setActiveClinicId,
        canViewAllLocations,
        allLocationsActive,
        setAllLocationsActive,
        viewedClinicIds,
        login,
        logout: handleLogout,
        updateUserProfile,
        refetchMyClinics,
        refetchCurrentUser,
        numPendingClinicInvitations,
        refetchNumPendingClinicInvitations,
        joinRequest,
        joinRequestLookupFailed,
        refetchJoinRequest,
      }}
    >
      {children}
      <SessionTimeoutModal
        isOpen={showWarning}
        countdown={countdown}
        onStayLoggedIn={stayLoggedIn}
      />
    </AuthContext.Provider>
  );
}

// useAuth is the context's public API; moving it to its own file would churn
// ~45 imports and every test that mocks '@/contexts/AuthContext'.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
